import { useAppSelector } from "core/helpers";
import { useBaseAction, useHandleModals } from "hooks/core";
import { useEffect, useState } from "react";
import { API } from "../../constants/RestApi";
import tenantsActions from "../../components/tenants/constants/tenantActions";
import { isGlobalAdmin } from "../../components/tenants/helpers/isGlobalAdmin";
import { ITenant } from "../../components/tenants/types";

type TProps = {
  fetchOnLoad: boolean;
};

const useTenantManagement = ({ fetchOnLoad }: TProps) => {
  const { request } = useBaseAction();
  const { currentUser } = useAppSelector((state) => state.app);
  const { modals, toggleModal, setSelectedEntity, selectedEntity, setModalTitle, modalTitle } =
    useHandleModals<ITenant, typeof tenantsActions[number]>({
      modalsKeys: tenantsActions,
    });
  const [tenants, setTenants] = useState<ITenant[]>([]);

  const getTenants = async (filter?: string, orderBy?: string) => {
    const response = (await request({
      method: "GET",
      url: API.ADMIN_TENANTS,
      useLoader: true,
      params: {
        $filter: filter || `isDeleted eq false`,
        $orderby: orderBy || "name asc",
      },
    })) as ITenant[];

    setTenants(response);
  };

  useEffect(() => {
    if (fetchOnLoad && isGlobalAdmin(currentUser)) getTenants();
  }, [currentUser]);

  const createTenant = async (tenant: ITenant) => {
    const { name } = tenant;
    await request({
      method: "POST",
      url: API.ADMIN_TENANTS,
      body: { name },
      successMessage: `Tenant ${tenant.name} has been created`,
      useLoader: true,
    });
    await getTenants();
  };

  const updateTenant = async (tenant: ITenant) => {
    await request({
      method: "PUT",
      url: API.ADMIN_TENANTS,
      body: tenant,
      successMessage: `Tenant ${tenant.name} has been updated`,
      useLoader: true,
    });
    await getTenants();
  };

  const toggleTenantStatus = async (tenant: ITenant) => {
    await request({
      method: "PUT",
      url: API.ADMIN_TENANTS,
      body: { ...tenant, isActive: !tenant.isActive },
      successMessage: `Tenant ${tenant.name} has been ${
        tenant.isActive ? "deactivated" : "activated"
      }`,
      useLoader: true,
    });
    await getTenants();
  };

  const deleteTenant = async (tenant: ITenant) => {
    setTenants((prev) => prev.filter((t) => t.id !== tenant.id));

    try {
      await request({
        method: "DELETE",
        url: API.ADMIN_TENANTS,
        body: {
          id: tenant.id,
          rowVersion: tenant.rowVersion || "",
        },
        successMessage: `Tenant ${tenant.name} has been deleted`,
        useLoader: true,
      });
      toggleModal({ key: "deleteTenant", value: false });
      setSelectedEntity(null);
    } catch (error) {
      setTenants((prev) => [...prev, tenant]);
      console.error("Error deleting tenant:", error);
      throw error;
    }
  };

  const handleTenantAction = (
    modalKey: string,
    tenant: ITenant,
    event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (event) event.preventDefault();

    let title = "";

    switch (modalKey) {
      case "editTenant":
        title = `Edit Tenant ${tenant.name}`;
        setSelectedEntity(tenant);
        setModalTitle(title);
        toggleModal({ key: modalKey, value: true });
        break;
      case "toggleTenantStatus":
        title = `Are you sure that you want to ${
          tenant.isActive ? "deactivate" : "activate"
        } tenant ${tenant.name}?`;
        setSelectedEntity(tenant);
        setModalTitle(title);
        toggleModal({ key: modalKey, value: true });
        break;
      case "deleteTenant":
        title = `Are you sure that you want to delete tenant ${tenant.name}?`;
        setSelectedEntity(tenant);
        setModalTitle(title);
        toggleModal({ key: modalKey, value: true });
        break;
      case "addTenant":
        title = "Add new Tenant";
        setSelectedEntity(null);
        setModalTitle(title);
        toggleModal({ key: modalKey, value: true });
        break;
      default:
        break;
    }
  };

  const handleConfirmTenantAction = async () => {
    if (!selectedEntity) return;

    try {
      if (modals.toggleTenantStatus) {
        await toggleTenantStatus(selectedEntity);
        toggleModal({ key: "toggleTenantStatus", value: false });
      } else if (modals.deleteTenant) {
        await deleteTenant(selectedEntity);
      }
      setSelectedEntity(null);
    } catch (error) {
      console.error("Error performing tenant action:", error);
    }
  };

  return {
    tenants,
    getTenants,
    createTenant,
    updateTenant,
    toggleTenantStatus,
    deleteTenant,
    modals,
    toggleModal,
    handleTenantAction,
    selectedEntity,
    modalTitle,
    handleConfirmTenantAction,
  };
};

export default useTenantManagement;
